import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { fetchWithRetry } from '@/lib/apiRequest';

import {
  buildPublicationInboxQueryString,
  type PublicationInboxAttention,
  type PublicationInboxFolder,
  type PublicationInboxState,
} from '@/lib/publicationInboxPresentation';

export type PublicationInboxItem = {
  id: string;
  publication: {
    title: string;
    processNumber: string | null;
    tribunal: string | null;
    source: string | null;
    sourceName: string | null;
    availableAt: string | null;
    publishedAt: string | null;
    capturedAt: string;
    excerpt: string | null;
    legacyStatus: string;
  };
  context: {
    caseId: string | null;
    caseTitle: string | null;
    caseStatus: string | null;
    clientId: string | null;
    clientName: string | null;
    procedureId: string | null;
    matchType: string | null;
    matchConfidence: number | null;
    matchConfirmed: boolean;
  };
  workflow: {
    state: PublicationInboxState;
    attention: PublicationInboxAttention;
    active: boolean;
  };
  ai: {
    jobStatus: string | null;
    latestRunStatus: string | null;
    runId: string | null;
    runStatus: string | null;
    priority: string | null;
    confidence: string | null;
    summary: string | null;
    requiresAction: string | null;
    actionType: string | null;
    suggestedAction: string | null;
    deadlineSource: string | null;
    deadlineValue: number | null;
    deadlineUnit: string | null;
    reviewId: string | null;
    reviewDecision: string | null;
  };
  operation: {
    state: string | null;
    operationalizationId: string | null;
    deadlineId: string | null;
    deadlineDueDate: string | null;
    deadlineStatus: string | null;
    taskId: string | null;
    taskStatus: string | null;
  };
  flags: {
    isNew: boolean;
    possibleDeadline: boolean;
    hearing: boolean;
    unlinked: boolean;
    exception: boolean;
    reviewPending: boolean;
    aiFailed: boolean;
  };
  badges: string[];
};

export type PublicationInboxSummary = {
  active: number;
  today: number;
  new: number;
  review: number;
  action: number;
  deadlines: number;
  hearings: number;
  unlinked: number;
  exceptions: number;
  completed: number;
};

export type PublicationInboxQuery = {
  folder: PublicationInboxFolder;
  search?: string;
  tribunal?: string;
  source?: string;
  attention?: PublicationInboxAttention | '';
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  cursor?: string;
};

type PublicationInboxResponse = {
  success: boolean;
  schemaVersion: string;
  summary: PublicationInboxSummary;
  items: PublicationInboxItem[];
  pagination: {
    limit: number;
    cursor: string;
    nextCursor: string | null;
    total: number;
    scanned: number;
    truncated: boolean;
  };
  generatedAt: string;
  error?: string;
};

const emptySummary: PublicationInboxSummary = {
  active: 0,
  today: 0,
  new: 0,
  review: 0,
  action: 0,
  deadlines: 0,
  hearings: 0,
  unlinked: 0,
  exceptions: 0,
  completed: 0,
};


export function usePublicationInbox(
  query: PublicationInboxQuery
) {
  const queryString =
    buildPublicationInboxQueryString(
      query
    );

  const [items, setItems] = useState<
    PublicationInboxItem[]
  >([]);
  const [summary, setSummary] =
    useState<PublicationInboxSummary>(
      emptySummary
    );
  const [pagination, setPagination] =
    useState<PublicationInboxResponse['pagination']>(
      {
        limit: query.limit ?? 5,
        cursor: '0',
        nextCursor: null,
        total: 0,
        scanned: 0,
        truncated: false,
      }
    );
  const [generatedAt, setGeneratedAt] =
    useState<string | null>(null);
  const [isLoading, setIsLoading] =
    useState(true);
  const [error, setError] =
    useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithRetry(
        `/api/publications/inbox?${queryString}`,
        {},
        {
          timeoutMs: 15000,
          retries: 1,
          retryDelayMs: 300,
        }
      );

      const data =
        (await response.json()) as PublicationInboxResponse;

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
            'Não foi possível carregar a Inbox Jurídica.'
        );
      }

      setItems(data.items ?? []);
      setSummary(data.summary ?? emptySummary);
      setPagination(data.pagination);
      setGeneratedAt(data.generatedAt);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Não foi possível carregar a Inbox Jurídica.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    items,
    summary,
    pagination,
    generatedAt,
    isLoading,
    error,
    refresh,
  };
}